import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { CoreAnalytics } from '@core-modules/analytics/providers/core-analytics';
import { ClientDetailsProvider } from '@core-modules/client-details/providers/client-details';
import { CoreDialogController } from '@core-modules/providers/core-dialogs/core-dialog-controller';
import { DeviceProvider } from '@core-modules/providers/device/device';
import { NavController } from '@ionic/angular';
import { AdvisorsProvider } from 'advisors/providers/advisors-provider';
import { AuthProvider } from 'authorization/providers/auth/auth-provider';
import { NewsProvider } from 'news/providers/news-provider';
import { RewardsProvider } from 'rewards/providers/rewards-provider';
import { IntercomSupportProvider } from 'user-settings/providers/intercom-support';
import { UserSettingsProvider } from 'user-settings/providers/user-settings';
import { PortfolioSummary } from 'valuations/models/portfolio-summary';
import { ValuationsProvider } from 'valuations/providers/valuations-provider';
import { HomePageData } from './home.page.data';


@Injectable({
    providedIn: 'root'
})
export class HomeResolverService  {

    /**
     * Class constructor
     */
    constructor
        (
            private advisors: AdvisorsProvider,
            private auth: AuthProvider,
            private news: NewsProvider,
            private rewards: RewardsProvider,
            private settings: UserSettingsProvider,
            private valuations: ValuationsProvider,
            private coreDialog: CoreDialogController,
            private clientDetails: ClientDetailsProvider,
            private device: DeviceProvider,
            public navCtrl: NavController,
            private intercom: IntercomSupportProvider,
            private analytics: CoreAnalytics
        )
    {

    }

    //#region Properties

    private index: { [ id: string ]: HomePageData } = {};

    //#endregion

    //#region Methods

    /**
     * Resolve method implementation
     * @param route 
     * @returns HomePageData data
     */
    async resolve(route: ActivatedRouteSnapshot): Promise<HomePageData>
    {
        let userIdentity = await this.auth.getUserIdentity();

        if (this.index[ userIdentity.sessionToken ])
        {
            return Promise.resolve(this.index[ userIdentity.sessionToken ]);
        }

        this.coreDialog.showLoader();
        let coordinates = await this.device.getUserLocation();
        let currentCountry: string = null;

        if (coordinates !== null)
        {
            let mobileUserLocation = await this.clientDetails.getUserLocation(coordinates.lat.toString(), coordinates.lng.toString());
            currentCountry = mobileUserLocation.CountryID;
        } else
        {
            let mobileUserLocation = await this.clientDetails.getUserLocation(null, null);
            currentCountry = mobileUserLocation.CountryID;
        }

        // await all of the promises to resolve
        let results = await Promise.all([
            this.getPortfolioSummary(userIdentity.sessionToken),
            this.advisors.getAdvisors(userIdentity.sessionToken),
            this.news.getNewsItems(),
            this.settings.getMobileUserSettings(userIdentity.sessionToken),
            this.settings.getCurrencies(),
            this.rewards.getCardDetails(userIdentity.sessionToken),
            this.rewards.getRewardsCategoryList(userIdentity.sessionToken, currentCountry),
            this.rewards.getFeaturedRewards(userIdentity.sessionToken, currentCountry),
            this.clientDetails.getCountries(),
        ]);

        let homePageData: HomePageData = {
            PortfolioSummary: results[ 0 ],
            Advisors: results[ 1 ],
            NewsItems: results[ 2 ],
            MobileUserSettings: results[ 3 ],
            Currencies: results[ 4 ],
            RewardCardDetails: results[ 5 ],
            RewardsCategories: results[ 6 ],
            FeaturedRewards: results[ 7 ],
            Countries: results[ 8 ],
            UserCountry: currentCountry
        }

        this.index[ userIdentity.sessionToken ] = homePageData;

        //set user hash
        this.analytics.SetUserId(homePageData.MobileUserSettings.ClientHash);

        // Initialize the Intercom support
        await this.intercom.initializeIntercomSupport
                            (
                                homePageData.MobileUserSettings.ClientHash, 
                                homePageData.MobileUserSettings.AccountEmail, 
                                `${homePageData.MobileUserSettings.FirstName} ${homePageData.MobileUserSettings.LastName}`,
                                homePageData.MobileUserSettings.IntercomUserHash, 
                            );
        
        // Hide the loader
        this.coreDialog.hideLoader();

        // Resolve the page data
        return homePageData;
    }

    /**
    * Get the portfolio summary for the application
    * @param sessionToken  
    */
    private async getPortfolioSummary(sessionToken): Promise<PortfolioSummary>
    {
        // try to get the data for the mobile application through
        // the default client ID and demo account settings
        let portfolioSummary = await this.valuations.getPortfolioSummary(sessionToken);
        if (portfolioSummary.PolicyList.length > 0)
        {
            return Promise.resolve(portfolioSummary);
        } else
        {
            // client has no data to show 
            return Promise.resolve(null);
        }
    }

    /**
     * Update the client's portfolio data, usually used whenever the client changes
     * the currency
     * @param sessionToken token of the client
     */
    public async updatePortfolioClientData(sessionToken: string)
    {
        let data = await this.getPortfolioSummary(sessionToken)
        this.index[ sessionToken ].PortfolioSummary = data;
        return this.index[ sessionToken ];
    }

    //#endregion
}